/**
 * Turning an app's roles on in a workspace (ADR-034 activation): an owner
 * gives the first people their roles, and from then on the app's data follows
 * roles, not workspace tiers. One write — the assignments, what each role may
 * do, and "roles are on" land together or not at all.
 *
 * Offered: every active member, with the roles the app SUGGESTS for their tier
 * ticked — never a sensitive one, and never above member level (ADR-034
 * §Seeding); those the owner ticks deliberately. Only
 * roles their tier may hold are offered (the server re-checks eligibility on
 * every request). Giving a sensitive role asks the owner to confirm it's
 * them: it is granted by the owner alone, recorded as such and the other
 * owners told — or, where the workspace wants a second person for owners too,
 * proposed for one. So an app whose roles are all sensitive can be turned on.
 *
 * A large workspace: one activation names at most
 * APP_ACCESS_ACTIVATION_SEED_MAX assignments (the server's cap). When the
 * suggestions alone would tick more, the panel summarises them instead of
 * listing everyone, and turning roles on asks the server to give everyone
 * the roles the app suggests for their tier (`seedSuggested`); anyone's roles
 * can be changed once roles are on. Below the cap the list is shown a page at
 * a time, with a name filter.
 *
 * Where the app places its records in audiences (ADR-034 phase 2), the
 * records the workspace wrote before roles were on are in none: the owner
 * chooses, per collection, which audience they go to — they are placed as
 * roles turn on, and those collections stay closed until they are.
 *
 * Used on the Access screen (an app approved before it declared roles, or
 * whose setup was left unfinished) and straight after approving an app that
 * declares roles.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { Link } from 'react-router-dom';
import {
  APP_ACCESS_ACTIVATION_SEED_MAX,
  type AppAccessActivationSetupDto,
  type AppAccessCollectionRecordsDto,
  type AppAccessScreenDto,
  type AppAccessTierDto,
  type VaultHolderMemberDto,
} from '@/lib/api';
import { StepUpDialog } from '../../components/StepUpDialog';
import {
  isAccessPolicyChanged,
  useActivateAppRoles,
  type AppAccessScope,
} from '../../hooks/useAppAccess';
import { AppText } from './AttributedText';
import { PlacementChooser } from './AudiencesSection';
import { MemberFilter, MemberPager, useMemberPage } from './members';
import {
  appAccessErrorCode,
  appAccessSectionPath,
  describeAppAccessError,
  memberLabel,
  roleLabel,
  tierMeets,
} from './shared';

interface SetupRolesPanelProps {
  scope: AppAccessScope;
  appName: string;
  access: AppAccessScreenDto['access'];
  members: readonly VaultHolderMemberDto[];
  /** Where the app's records sit here by audience (phase 2); those in none need a place. */
  records?: readonly AppAccessCollectionRecordsDto[];
  onActivated?: () => void;
  /** Shown beside Turn on, e.g. "Later" in the approval flow. */
  secondaryAction?: React.ReactNode;
}

const key = (principalId: string, roleId: string) => `${principalId}:${roleId}`;

/**
 * The tiers whose suggestions are ticked — and seeded by the server for
 * `seedSuggested` (its own SEED_SUGGESTED_TIERS, pinned by
 * app-access-client-parity.test.ts): suggestions are never pre-selected
 * above member level; an admin's or owner's wait for a person to give them.
 */
const SEED_SUGGESTED_TIERS: readonly AppAccessTierDto[] = ['viewer', 'member'];

/** As the summary lists them, from the top. */
const TIER_PLURAL: Record<AppAccessTierDto, string> = {
  owner: 'Owners',
  admin: 'Admins',
  member: 'Members',
  viewer: 'Viewers',
};

/**
 * The roles the app suggests for a tier that a member of it may hold — never
 * a sensitive one, and none above member level; those the owner gives
 * deliberately. The server's rule for `seedSuggested`, so the summary says
 * what it will do.
 */
function suggestedRolesFor(access: AppAccessScreenDto['access'], tier: AppAccessTierDto): string[] {
  if (!SEED_SUGGESTED_TIERS.includes(tier)) return [];
  return (access.suggestions[tier] ?? []).filter((roleId) => {
    const role = access.roles.find((r) => r.roleId === roleId);
    return !!role && !role.sensitive && tierMeets(tier, role.minTier);
  });
}

export function SetupRolesPanel({
  scope,
  appName,
  access,
  members,
  records = [],
  onActivated,
  secondaryAction,
}: SetupRolesPanelProps) {
  const activate = useActivateAppRoles(scope);
  const unplaced = records.filter((r) => r.unplaced > 0);
  const [placements, setPlacements] = useState<Record<string, string>>({});
  const unplacedLeft = unplaced.filter((r) => !placements[r.collection]);
  const active = useMemo(() => members.filter((m) => m.status === 'active'), [members]);
  const seedable = access.roles;
  const sensitiveRoles = new Set(access.roles.filter((r) => r.sensitive).map((r) => r.roleId));
  // What the app suggests: ticked for the owner to review — or, past what one
  // activation may name, summarised and seeded by the server.
  const suggested = useMemo(() => {
    const seed = new Set<string>();
    for (const m of active) {
      for (const roleId of suggestedRolesFor(access, m.role)) seed.add(key(m.principalId, roleId));
    }
    return seed;
  }, [active, access]);
  const summary = suggested.size > APP_ACCESS_ACTIVATION_SEED_MAX;
  const [chosen, setChosen] = useState<Set<string>>(() =>
    summary ? new Set() : new Set(suggested),
  );
  const page = useMemberPage(active);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Roles went on but the earlier records are not all placed yet (phase 2):
  // say so, rather than reporting success and closing.
  const [unfinished, setUnfinished] = useState<AppAccessActivationSetupDto | null>(null);
  // How many roles the server gave from the app's suggestions (`seedSuggested`).
  const [seeded, setSeeded] = useState(0);
  const done = (result: {
    setup?: AppAccessActivationSetupDto;
    seededFromSuggestions?: number;
  }) => {
    const setup = result.setup;
    const fromSuggestions = result.seededFromSuggestions ?? 0;
    setSeeded(fromSuggestions);
    if (setup && (setup.placementRequired.length > 0 || setup.error)) {
      setUnfinished(setup);
      return;
    }
    toast({
      title: `${appName}’s roles are on`,
      ...(fromSuggestions > 0 ? { description: seededLine(fromSuggestions, appName) } : {}),
    });
    onActivated?.();
  };
  const overCap = !summary && chosen.size > APP_ACCESS_ACTIVATION_SEED_MAX;
  // The roles this panel shows: turning them on is refused if a publish
  // changed them since (ACCESS_POLICY_CHANGED), never done unseen.
  const expectedAccessPolicyId = access.policyId;

  const toggle = (k: string, on: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (on) next.add(k);
      else next.delete(k);
      return next;
    });

  const seed = () =>
    [...chosen].map((k) => {
      const [principalId, roleId] = k.split(':');
      return { principalId, roleId };
    });
  // The suggestions are never sensitive, so a summarised seed needs no step-up.
  const seedsSensitive = !summary && [...chosen].some((k) => sensitiveRoles.has(k.split(':')[1]));

  async function turnOn() {
    setError(null);
    if (seedsSensitive) {
      setConfirming(true);
      return;
    }
    try {
      done(
        await activate.mutateAsync(
          summary
            ? { assignments: [], seedSuggested: true, placements, expectedAccessPolicyId }
            : { assignments: seed(), placements, expectedAccessPolicyId },
        ),
      );
    } catch (err) {
      setError(describeAppAccessError(err));
    }
  }

  if (unfinished) {
    const stillPlacing = appAccessErrorCode(unfinished.error) === 'PLACEMENT_IN_PROGRESS';
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium">Roles are on — setup is not finished</p>
        {seeded > 0 && <p>{seededLine(seeded, appName)}</p>}
        <p className="text-muted-foreground">
          {stillPlacing
            ? `${appName} has many records from before; placing them carries on by itself on its Access screen.`
            : unfinished.error
              ? describeAppAccessError(unfinished.error)
              : `Some of ${appName}’s records from before still need a place (${unfinished.placementRequired.join(', ')}).`}{' '}
          Until they have one, its data with audiences stays closed to everyone.
        </p>
        <Link
          className="underline"
          to={appAccessSectionPath(scope.holderId, scope.archeId, 'people')}
        >
          Finish on the Access screen
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm">
      <p className="text-muted-foreground">
        Once roles are on, people use {appName}’s data in this workspace only through the roles they
        hold — their workspace role (owner, admin, member, viewer) no longer decides it. Choose who
        starts with which role; you can change it any time.
      </p>
      {seedable.length === 0 ? (
        <p className="text-muted-foreground">This app declares no roles to give.</p>
      ) : summary ? (
        <SuggestedSeedSummary
          appName={appName}
          access={access}
          active={active}
          people={new Set([...suggested].map((k) => k.split(':')[0])).size}
        />
      ) : (
        <div className="space-y-2">
          <MemberFilter page={page} id="setup-roles-find" />
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 pr-4 font-medium">Person</th>
                  {seedable.map((r) => (
                    <th key={r.roleId} className="py-2 pr-4 font-medium">
                      <AppText appName={appName}>{r.label}</AppText>
                      {r.sensitive && (
                        <span className="ml-1 text-xs font-normal text-destructive">
                          (sensitive)
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.rows.map((m) => (
                  <tr key={m.principalId} className="border-b border-border/60">
                    <td className="py-2 pr-4">
                      {memberLabel(m, m.principalId)}{' '}
                      <span className="text-xs capitalize text-muted-foreground">{m.role}</span>
                    </td>
                    {seedable.map((r) => {
                      const k = key(m.principalId, r.roleId);
                      const eligible = tierMeets(m.role, r.minTier);
                      return (
                        <td key={r.roleId} className="py-2 pr-4">
                          <Checkbox
                            aria-label={`${r.label} for ${memberLabel(m, m.principalId)}`}
                            checked={chosen.has(k)}
                            disabled={!eligible || activate.isPending}
                            title={eligible ? undefined : `Needs at least ${r.minTier}`}
                            onCheckedChange={(v) => toggle(k, v === true)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <MemberPager page={page} />
        </div>
      )}
      {unplaced.length > 0 && (
        <div className="space-y-2">
          <p className="font-medium">Where existing records go</p>
          <p className="text-muted-foreground">
            {appName} places its records in audiences — readerships it declares — and only people
            whose roles read an audience see its records. Records from before roles were on are in
            none yet: choose who reads each kind. Until they are placed, that data stays closed.
          </p>
          {unplaced.map((r) => (
            <PlacementChooser
              key={r.collection}
              records={r}
              audiences={access.audiences}
              appName={appName}
              value={placements[r.collection]}
              disabled={activate.isPending}
              onChange={(audience) =>
                setPlacements((prev) => ({ ...prev, [r.collection]: audience }))
              }
            />
          ))}
        </div>
      )}
      {seedsSensitive && (
        <p className="text-muted-foreground">
          You are giving a sensitive role: you will confirm it’s you, it is recorded as granted by
          you alone, and the workspace’s other owners are told.
        </p>
      )}
      {confirming && (
        <StepUpDialog
          open
          onOpenChange={(o) => !o && setConfirming(false)}
          title="Give a sensitive role"
          description="Confirm it’s you to turn roles on with a sensitive role."
          confirmLabel="Turn on roles"
          onConfirm={async (creds) => {
            try {
              done(
                await activate.mutateAsync({
                  assignments: seed(),
                  placements,
                  expectedAccessPolicyId,
                  creds,
                }),
              );
            } catch (err) {
              // The screen reloads (the mutation settles); say why, in words.
              if (isAccessPolicyChanged(err)) throw new Error(describeAppAccessError(err));
              throw err;
            }
          }}
          onSuccess={() => setConfirming(false)}
        />
      )}
      {error && <p className="text-destructive">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={turnOn}
          disabled={
            (!summary && chosen.size === 0) ||
            overCap ||
            unplacedLeft.length > 0 ||
            activate.isPending
          }
        >
          {activate.isPending ? 'Turning on…' : 'Turn on roles'}
        </Button>
        {secondaryAction}
        {!summary && chosen.size === 0 && seedable.length > 0 && (
          <span className="text-muted-foreground">Give at least one person a role.</span>
        )}
        {overCap && (
          <span className="text-muted-foreground">
            Turning roles on gives at most {APP_ACCESS_ACTIVATION_SEED_MAX} roles at once — you have
            chosen {chosen.size}. Give the rest once roles are on.
          </span>
        )}
        {(summary || chosen.size > 0) && !overCap && unplacedLeft.length > 0 && (
          <span className="text-muted-foreground">Choose where every kind of record goes.</span>
        )}
      </div>
    </div>
  );
}

/** "600 roles were given from what Ledger suggests." */
function seededLine(count: number, appName: string): string {
  return `${count === 1 ? '1 role was' : `${count} roles were`} given from what ${appName} suggests for each workspace role.`;
}

/**
 * Who would start with which role, for a workspace too large to list person
 * by person: the server gives each active member the roles the app suggests
 * for their workspace role — the same rule as the ticks below the cap.
 */
function SuggestedSeedSummary({
  appName,
  access,
  active,
  people,
}: {
  appName: string;
  access: AppAccessScreenDto['access'];
  active: readonly VaultHolderMemberDto[];
  people: number;
}) {
  const byTier = [...SEED_SUGGESTED_TIERS]
    .reverse()
    .map((tier) => ({
      tier,
      count: active.filter((m) => m.role === tier).length,
      roles: suggestedRolesFor(access, tier),
    }))
    .filter((t) => t.count > 0 && t.roles.length > 0);
  return (
    <div className="space-y-2 rounded-md border border-border p-3" data-testid="seed-summary">
      <p>
        {people === 1 ? '1 person' : `${people} people`} will get the roles {appName} suggests for
        their workspace role; you can change anyone’s roles after turning roles on.
      </p>
      <ul className="list-disc pl-5 text-muted-foreground">
        {byTier.map((t) => (
          <li key={t.tier}>
            {TIER_PLURAL[t.tier]} ({t.count}):{' '}
            {t.roles.map((r, i) => (
              <span key={r}>
                {i > 0 && ', '}
                <AppText appName={appName}>{roleLabel(access.roles, r)}</AppText>
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}
